/**
 * Loading UI shared by Search Programs and Universities.
 *
 * Both pages already compute exactly the state needed to drive this: `data`
 * (null until the first response for the current search ever lands), `loading`
 * and `stale` (a request for the current filters is in flight — see the
 * `bodyKey`/`resultsKey` comment in each hook) and `error`. This file adds no
 * new state and makes no request of its own; it only renders one of two
 * visuals from state the pages already hold, so a slow first load never shows
 * an empty results area and a filter change never lets an old result set read
 * as final.
 */
function Spinner({ size = 28 }) {
  return (
    <svg
      className="crizac-spinner"
      width={size}
      height={size}
      viewBox="0 0 50 50"
      aria-hidden="true"
      focusable="false"
    >
      <circle className="crizac-spinner-track" cx="25" cy="25" r="20" fill="none" strokeWidth="5" />
      <circle className="crizac-spinner-arc" cx="25" cy="25" r="20" fill="none" strokeWidth="5" />
    </svg>
  );
}

const SKELETON_CARD_COUNT = 6;
const SKELETON_ROW_COUNT = 6;

/**
 * Shown in place of the results area before the first response for the
 * current search has come back — i.e. exactly when the page would otherwise
 * render nothing (`data` is still null) and look empty or broken.
 */
export function InitialLoader({ label, view = 'card' }) {
  return (
    <div className="initial-loader" role="status" aria-live="polite" aria-busy="true">
      <Spinner size={36} />
      <div className="initial-loader-label">{label}</div>
      <div
        className={`initial-loader-skeleton ${view === 'list' ? 'is-rows' : 'is-cards'}`}
        aria-hidden="true"
      >
        {view === 'list'
          ? Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
            <div className="skeleton-row" key={i} />
          ))
          : Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
            <div className="skeleton-card" key={i} />
          ))}
      </div>
    </div>
  );
}

/**
 * A thin overlay over an already-populated results area while a filter,
 * search, sort or pagination request is in flight. The previous result set
 * stays mounted underneath (dimmed via the `.is-stale` class the page applies
 * to the wrapper) so nothing collapses or flashes empty — this just makes it
 * clear those rows are not the answer to the request that is currently running.
 */
export function ResultsOverlay({ active, label = 'Updating results…' }) {
  if (!active) return null;
  return (
    <div className="results-overlay" role="status" aria-live="polite" aria-busy="true">
      <Spinner size={28} />
      <div className="results-overlay-label">{label}</div>
    </div>
  );
}
